import { useState, useCallback } from 'react';
import { updateProfile } from './updateProfile';
import { updatePassword } from './updatePassword';
import type { UpdateProfilePayload, UpdatePasswordPayload } from '@/@types/account';

/**
 * Custom hook to manage account update operations
 * Provides loading states and error handling for profile and password updates
 */
export function useManageAccount() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /**
     * Update user profile (full name)
     */
    const handleUpdateProfile = useCallback(
        async (payload: UpdateProfilePayload) => {
            setLoading(true);
            setError(null);

            try {
                return await updateProfile(payload);
            } catch (e: any) {
                const errorMessage = e?.message || 'Failed to update profile';
                setError(errorMessage);
                throw e;
            } finally {
                setLoading(false);
            }
        },
        []
    );

    /**
     * Change user password
     */
    const handleUpdatePassword = useCallback(
        async (payload: UpdatePasswordPayload) => {
            setLoading(true);
            setError(null);

            try {
                return await updatePassword(payload);
            } catch (e: any) {
                const errorMessage = e?.message || 'Failed to change password';
                setError(errorMessage);
                throw e;
            } finally {
                setLoading(false);
            }
        },
        []
    );

    return {
        updateProfile: handleUpdateProfile,
        updatePassword: handleUpdatePassword,
        loading,
        error,
    };
}

